import { NodeProps } from 'reactflow';
import { twMerge } from 'tailwind-merge';

import { Icon, RyeconProjects } from '@dbt-labs/sourdough';

import { InteractionDagNode } from '../../dag/internalDag';
import { DbtDagNode } from '../../dbt-dag';
import { useResourceNodeColors } from '../../hooks/useResourceNodeColor';
import { AccessLevelIcon, accessToSentenceCase, accessTypes } from '../AccessLevelIcon';
import { DbtResourceIcon, ResourceTypeExplorer } from '../ResourceTypeIcon';

export const getSublabel = <TDbtDagNode extends DbtDagNode>({
  sublabel,
  access,
}: InteractionDagNode<TDbtDagNode>) => {
  if (sublabel != null) return { sublabel };
  if (accessTypes.includes(access as any)) return { access };
  return {};
};

type SublabelProps<TDbtDagNode extends DbtDagNode> = NodeProps<
  InteractionDagNode<TDbtDagNode>
> & {
  resourceType: ResourceTypeExplorer | 'unknown';
  shouldRenderColumn: boolean;
};

export const Sublabel = <TDbtDagNode extends DbtDagNode>({
  data,
  resourceType,
  shouldRenderColumn,
}: SublabelProps<TDbtDagNode>) => {
  const { sublabel, access } = getSublabel(data);
  const sublabelBackgroundColorClass = useResourceNodeColors({
    resourceType: resourceType,
    showColor: true,
  });
  const isExternalProject = data.isExternalNode && data.resourceType === 'project';
  const isExternalResource = data.isExternalNode && data.resourceType !== 'project';
  const shouldShowLabel =
    // if it's an external resource, we show render project name
    // if it's an external project, we show the access level if the user does not have permission
    // if it has a sublabel or it's a column lineage, we render the sublabel, resource type and maybe the project name if it's external
    isExternalResource ||
    (isExternalProject && access === 'no_permission') ||
    sublabel ||
    shouldRenderColumn;
  if (!shouldShowLabel) return null;

  return (
    <div className="block overflow-hidden text-ellipsis whitespace-nowrap py-2 text-base">
      <div className="pb-1">{sublabel != null && sublabel}</div>
      {isExternalResource && (
        <div className="flex w-fit items-center rounded">
          <Icon ryecon={RyeconProjects} size="sm" className="mr-1" />
          <span>
            {data.environmentDeploymentType
              ? `[${data.environmentDeploymentType}] `
              : ''}
            {data.dbtCloudProject}
          </span>
        </div>
      )}
      {shouldRenderColumn && (
        <div
          className={twMerge(
            'mt-2 flex w-fit items-center rounded px-2 py-1 text-fgBlack',
            sublabelBackgroundColorClass,
          )}
        >
          <DbtResourceIcon resource={resourceType} className="mr-1.5 flex-1" />
          <span className="flex-1">{`${resourceType.toUpperCase()}`}</span>
        </div>
      )}
      {isExternalProject && access === 'no_permission' && (
        <>
          <span>
            <AccessLevelIcon
              tooltipConfig={{ hidden: true }}
              access={access}
              size="xs"
              className=""
              isSolid={false}
            />
            <span className="ml-1">{accessToSentenceCase(access)}</span>
          </span>
        </>
      )}
    </div>
  );
};
